import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Modal } from 'react-bootstrap';
import { CalendarEventFill } from 'react-bootstrap-icons';
import { getEvents } from '../services/EventScheduleService';
import { LoadingSpinner } from '../components/LoadingSpinner';

moment.updateLocale('en', { week: { dow: 1, doy: 4 } });
const localizer = momentLocalizer(moment);

const FORMATS = {
  timeGutterFormat:     'HH:mm',
  eventTimeRangeFormat: ({ start, end }, culture, loc) =>
    end - start <= 60000
      ? loc.format(start, 'HH:mm', culture)
      : `${loc.format(start, 'HH:mm', culture)} – ${loc.format(end, 'HH:mm', culture)}`,
  selectRangeFormat:    ({ start, end }, culture, loc) =>
    `${loc.format(start, 'HH:mm', culture)} – ${loc.format(end, 'HH:mm', culture)}`,
};

function getTextColor(hex) {
  if (!hex || hex.length < 7) return '#ffffff';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

function parseTime(timeStr) {
  if (!timeStr) return null;
  const parts = timeStr.split(':');
  return { h: parseInt(parts[0], 10), m: parseInt(parts[1], 10) };
}

// Returns current server time (UTC-2) expressed in the same local-slot
// convention that expandEvents uses — so it compares correctly with event.start.
function getServerNow() {
  const d = new Date();
  const utcMs = d.getTime() + (d.getTimezoneOffset() * 60000);
  const serverDate = new Date(utcMs + (3600000 * -2));
  const result = new Date();
  result.setFullYear(serverDate.getFullYear(), serverDate.getMonth(), serverDate.getDate());
  result.setHours(serverDate.getHours(), serverDate.getMinutes(), serverDate.getSeconds(), serverDate.getMilliseconds());
  return result;
}

function formatEventTime(event) {
  return `${moment(event.start).format('HH:mm')} – ${moment(event.end).format('HH:mm')}`;
}

function formatCountdown(event, now) {
  const diffMs = event.start - now;

  // Event is in the past or ongoing
  if (diffMs <= 0) {
    if (event.end > now) {
      const m = Math.ceil((event.end - now) / 60000);
      if (m < 60) return `Ongoing — ends in ${m}m`;
      const h = Math.floor(m / 60);
      const rem = m % 60;
      return rem > 0 ? `Ongoing — ends in ${h}h ${rem}m` : `Ongoing — ends in ${h}h`;
    }
    return 'Event has passed';
  }

  const totalMin  = Math.floor(diffMs / 60000);
  const totalHour = Math.floor(totalMin / 60);
  const totalDay  = Math.floor(totalHour / 24);
  const totalWeek = Math.floor(totalDay / 7);
  const totalMon  = Math.floor(totalDay / 30.44);
  const totalYear = Math.floor(totalDay / 365);

  if (totalMin < 1)   return 'Starting now';
  if (totalMin < 60)  return `Starts in ${totalMin}m`;

  if (totalHour < 24) {
    const m = totalMin % 60;
    return m > 0 ? `Starts in ${totalHour}h ${m}m` : `Starts in ${totalHour}h`;
  }

  if (totalDay < 7) {
    const h = totalHour % 24;
    return h > 0 ? `Starts in ${totalDay}d ${h}h` : `Starts in ${totalDay}d`;
  }

  if (totalDay < 30) {
    const d = totalDay % 7;
    return d > 0 ? `Starts in ${totalWeek}w ${d}d` : `Starts in ${totalWeek}w`;
  }

  if (totalDay < 365) {
    const w = Math.floor((totalDay % 30.44) / 7);
    return w > 0 ? `Starts in ${totalMon}mo ${w}w` : `Starts in ${totalMon}mo`;
  }

  const mo = totalMon % 12;
  return mo > 0 ? `Starts in ${totalYear}y ${mo}mo` : `Starts in ${totalYear}y`;
}

function countdownVariant(event, now) {
  const diffMs = event.start - now;
  if (diffMs <= 0) return event.end > now ? 'active' : 'passed';
  if (diffMs < 30 * 60000) return 'soon';
  return 'upcoming';
}

// ─── Hover Tooltip ────────────────────────────────────────────────────────────

function EventTooltip({ event, pos, serverNow }) {
  if (!event) return null;
  const res = event.resource || {};
  const color = res.Color || '#38bdf8';
  const textColor = getTextColor(color);
  const variant = countdownVariant(event, serverNow);

  const TOOLTIP_W = 280;
  const OFFSET = 16;
  let x = pos.x + OFFSET;
  let y = pos.y + OFFSET;
  if (x + TOOLTIP_W > window.innerWidth - 8) x = pos.x - TOOLTIP_W - OFFSET;
  if (y + 200 > window.innerHeight - 8) y = pos.y - 200 - OFFSET;

  return (
    <div className="es-tooltip" style={{ left: x, top: y }}>
      <div
        className="es-tooltip-header"
        style={{ backgroundColor: color, color: textColor }}
      >
        <span className="es-tooltip-title">{event.title}</span>
        {res.EventType && (
          <span className="es-tooltip-badge">{res.EventType}</span>
        )}
      </div>
      <div className="es-tooltip-body">
        <div className="es-tooltip-time">{formatEventTime(event)}</div>
        {res.Description && (
          <p className="es-tooltip-desc">{res.Description}</p>
        )}
        <div className={`es-countdown es-countdown--${variant}`}>
          {formatCountdown(event, serverNow)}
        </div>
      </div>
    </div>
  );
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────

function EventDetailModal({ event, onHide, serverNow }) {
  const res = event?.resource || {};
  const color = res.Color || '#38bdf8';
  const variant = event ? countdownVariant(event, serverNow) : 'upcoming';

  return (
    <Modal
      className="custom-modal es-detail-modal"
      show={!!event}
      onHide={onHide}
      centered
    >
      <Modal.Header closeButton>
        <div className="es-modal-title-row">
          <span className="es-modal-color-dot" style={{ backgroundColor: color }} />
          <Modal.Title>{event?.title}</Modal.Title>
        </div>
      </Modal.Header>
      <Modal.Body className="es-modal-body">
        {event && (
          <>
            <div className="es-modal-row">
              <span className="es-modal-label">Time</span>
              <span className="es-modal-value">{formatEventTime(event)}</span>
            </div>
            {res.EventType && (
              <div className="es-modal-row">
                <span className="es-modal-label">Type</span>
                <span
                  className="es-modal-value es-modal-tag"
                  style={{ '--es-tag-color': color }}
                >
                  {res.EventType}
                </span>
              </div>
            )}
            <div className={`es-modal-countdown es-countdown--${variant}`}>
              {formatCountdown(event, serverNow)}
            </div>
            {res.Description && (
              <>
                <div className="es-modal-divider" />
                <p className="es-modal-desc">{res.Description}</p>
              </>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

// ─── Calendar event components ───────────────────────────────────────────────
//
// Defined at module level so react-big-calendar never sees a changed component
// reference, which would cause it to unmount/remount every event on each render.
//
// _eventHandlers is written on every EventSchedule render so the components
// always call the latest closures without needing to be recreated.

const _eventHandlers = { over: null, out: null };

function EventWrapper({ event, children }) {
  const wrapRef = useRef(null);
  return (
    <div
      ref={wrapRef}
      onMouseOver={(e) => _eventHandlers.over?.(event, e)}
      onMouseOut={(e) => {
        if (!wrapRef.current?.contains(e.relatedTarget)) {
          _eventHandlers.out?.();
        }
      }}
    >
      {children}
    </div>
  );
}

function EventBlock({ event }) {
  return <div className="es-event-block">{event.title}</div>;
}

function CalendarToolbar({ label, view, views, onView }) {
  return (
    <div className="es-toolbar">
      <span className="es-toolbar-label">{label}</span>
      <div className="es-toolbar-views">
        {views.map(v => (
          <button
            key={v}
            type="button"
            className={`es-toolbar-view-btn${view === v ? ' es-toolbar-view-btn--active' : ''}`}
            onClick={() => onView(v)}
          >
            {v.charAt(0).toUpperCase() + v.slice(1)}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Event expansion helpers ──────────────────────────────────────────────────

function expandEvents(rawDefs, rangeStart, rangeEnd) {
  const result = [];
  const start = new Date(rangeStart);
  start.setHours(0, 0, 0, 0);
  const end = new Date(rangeEnd);
  end.setHours(23, 59, 59, 999);

  for (const def of rawDefs) {
    const resource = { Color: def.Color, EventType: def.EventType, Description: def.Description };

    for (const slot of def.Schedules || []) {
      if (slot.IsRecurring) {
        const recurDays = slot.RecurDays || 0;
        const startTime = parseTime(slot.RecurStart);
        if (!startTime || recurDays === 0) continue;

        const endTime = parseTime(slot.RecurEnd);
        const d = new Date(start);
        while (d <= end) {
          const dayBit = 1 << d.getDay();
          if (recurDays & dayBit) {
            const evStart = new Date(d);
            evStart.setHours(startTime.h, startTime.m, 0, 0);

            const evEnd = new Date(d);
            if (endTime) {
              evEnd.setHours(endTime.h, endTime.m, 0, 0);
              if (evEnd <= evStart) evEnd.setDate(evEnd.getDate() + 1);
            } else {
              evEnd.setHours(startTime.h + 1, startTime.m, 0, 0);
            }

            result.push({
              id: `r-${def.ID}-${slot.ID}-${d.toDateString()}`,
              title: def.Title,
              start: evStart,
              end: evEnd,
              resource,
            });
          }
          d.setDate(d.getDate() + 1);
        }
      } else if (slot.EventStart) {
        const evStart = new Date(slot.EventStart);
        const evEnd = slot.EventEnd ? new Date(slot.EventEnd) : new Date(evStart.getTime() + 3600000);
        if (evStart <= end && evEnd >= start) {
          result.push({
            id: `o-${def.ID}-${slot.ID}`,
            title: def.Title,
            start: evStart,
            end: evEnd,
            resource,
          });
        }
      }
    }
  }

  return result;
}

function getWeekBounds(date) {
  const start = new Date(date);
  const day = start.getDay(); // 0=Sun, 1=Mon, …, 6=Sat
  start.setDate(start.getDate() + (day === 0 ? -6 : 1 - day));
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return { rangeStart: start, rangeEnd: end };
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function EventSchedule() {
  const [rawEvents, setRawEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleRange, setVisibleRange] = useState(() => getWeekBounds(new Date()));
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [serverNow, setServerNow] = useState(getServerNow);

  const hoveredIdRef = useRef(null);

  // Keep module-level handler stubs current every render so EventWrapper
  // always calls fresh closures without the component itself being recreated.
  _eventHandlers.over = (calEvent, e) => {
    if (hoveredIdRef.current === calEvent.id) return;
    hoveredIdRef.current = calEvent.id;
    setHoveredEvent(calEvent);
    setTooltipPos({ x: e.clientX, y: e.clientY });
  };
  _eventHandlers.out = () => {
    if (hoveredIdRef.current === null) return;
    hoveredIdRef.current = null;
    setHoveredEvent(null);
  };

  // Tick the server clock every second for live countdown
  useEffect(() => {
    const id = setInterval(() => setServerNow(getServerNow()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const sidebar = document.getElementById('sidebarColumn');
    const main    = document.getElementById('contentColumn');

    sidebar.classList.add('hide');
    main.classList.remove('col-lg-8');
    main.classList.add('col-lg-12');

    return () => {
      sidebar.classList.remove('hide');
      main.classList.add('col-lg-8');
      main.classList.remove('col-lg-12');
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await getEvents();
        if (!cancelled) setRawEvents(data || []);
      } catch {
        // non-critical — leave calendar empty
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const calendarEvents = useMemo(
    () => expandEvents(rawEvents, visibleRange.rangeStart, visibleRange.rangeEnd),
    [rawEvents, visibleRange]
  );

  const handleRangeChange = useCallback((range) => {
    if (Array.isArray(range)) {
      setVisibleRange({ rangeStart: range[0], rangeEnd: range[range.length - 1] });
    } else {
      setVisibleRange({ rangeStart: range.start, rangeEnd: range.end });
    }
  }, []);

  const eventPropGetter = useCallback((event) => {
    const color = event.resource?.Color || '#38bdf8';
    return {
      style: {
        backgroundColor: color,
        borderColor: color,
        color: getTextColor(color),
        borderRadius: '4px',
        fontSize: '0.78em',
      },
    };
  }, []);

  const handleSelectEvent = useCallback((event) => {
    hoveredIdRef.current = null;
    setHoveredEvent(null);
    setSelectedEvent(event);
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="es-page">
      <div className="es-hero">
        <div className="es-hero-icon"><CalendarEventFill /></div>
        <h2>Event Schedule</h2>
        <p>Upcoming and recurring in-game events. All times are server time (UTC−2).</p>
        <div className="es-server-clock">
          <span className="es-server-clock-label">Server Time</span>
          <span className="es-server-clock-time">{moment(serverNow).format('dddd, MMMM D YYYY  —  HH:mm:ss')}</span>
        </div>
      </div>

      <div className="es-calendar">
        <Calendar
          localizer={localizer}
          events={calendarEvents}
          defaultView="week"
          views={['week', 'day']}
          onRangeChange={handleRangeChange}
          eventPropGetter={eventPropGetter}
          components={{ event: EventBlock, eventWrapper: EventWrapper, toolbar: CalendarToolbar }}
          onSelectEvent={handleSelectEvent}
          formats={FORMATS}
          popup
        />
      </div>

      <EventTooltip event={hoveredEvent} pos={tooltipPos} serverNow={serverNow} />

      <EventDetailModal
        event={selectedEvent}
        onHide={() => setSelectedEvent(null)}
        serverNow={serverNow}
      />
    </div>
  );
}
